/**
 * ResetDataSection — แท็บ "ล้างข้อมูล" ใน owner Settings (OWNER เท่านั้น).
 *
 * 2 ระดับ (ภาษาคน ไม่ใช่ Soft/Hard — ตาม feedback_ui_copy_plain_thai):
 *   - ล้างข้อมูลการทำงาน : ลบเงินเดือน/เวลา/ลา/OT/สลิป เก็บพนักงาน+ตั้งค่า
 *   - ล้างข้อมูลทั้งหมด   : + ลบพนักงาน+ตั้งค่า เหลือบริษัทเปล่า
 *
 * Non-HR UX safety: โชว์จำนวนที่จะลบจริง (ConsequenceBody) + เตือนถาวร
 * (ComplianceAlert block) + ต้องพิมพ์ชื่อบริษัทยืนยัน (กันกดพลาด). จำกัด tenant
 * ของตัวเองเสมอ (server บังคับซ้ำ). ลบจริงเป็น atomic ฝั่ง DB.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle } from 'lucide-react';
import { Modal } from './ui/Modal';
import { ComplianceAlert } from './ui/ComplianceAlert';
import { ConsequenceBody, type ConsequenceLine } from './ui/ConsequencePreview';
import { useToast } from './ui/toast';
import { useResetData, type ResetPreview } from '../hooks';
import { useAuthStore } from '../lib/store';
import { apiFetch } from '../lib/api-client';

type Mode = 'work' | 'all';

export function ResetDataSection() {
  const toast = useToast();
  const navigate = useNavigate();
  const reset = useResetData();
  const tenantName = useAuthStore((s) => s.profile?.tenant_name ?? '');

  const [mode, setMode] = useState<Mode | null>(null);
  const [preview, setPreview] = useState<ResetPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  async function openReset(m: Mode) {
    setLoading(true);
    setConfirmText('');
    try {
      const p = await apiFetch<ResetPreview>('/owner/reset-data/preview');
      setPreview(p);
      setMode(m);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }

  function close() {
    setMode(null);
    setPreview(null);
    setConfirmText('');
  }

  async function doReset() {
    if (!mode) return;
    try {
      await reset.mutateAsync({ mode, confirm: confirmText.trim() });
      close();
      if (mode === 'all') {
        // ล้างทั้งหมด → พนักงาน/ตั้งค่าหายหมด = ตั้งค่าใหม่ → พาเข้าตัวช่วยตั้งค่าเลย
        toast.success('ล้างข้อมูลทั้งหมดแล้ว — เริ่มตั้งค่าระบบใหม่ได้เลย');
        navigate('/owner/setup');
      } else {
        toast.success('ล้างข้อมูลการทำงานแล้ว');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ล้างข้อมูลไม่สำเร็จ');
    }
  }

  // เส้นสรุปจำนวนใน modal — work เสมอ, setup เฉพาะ "ทั้งหมด"
  const lines: ConsequenceLine[] = preview
    ? [
        ...preview.work.map((w) => ({ label: w.label, value: `${w.count.toLocaleString('th-TH')} รายการ`, tone: 'danger' as const })),
        ...(mode === 'all'
          ? preview.setup.map((s) => ({ label: s.label, value: `${s.count.toLocaleString('th-TH')} รายการ`, tone: 'danger' as const }))
          : []),
      ]
    : [];

  const confirmOk = confirmText.trim().length > 0;

  return (
    <div className="space-y-4 max-w-xl">
      <div className="card p-4 border-l-4 border-l-red-500">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-gray-900">ล้างข้อมูลของบริษัท</div>
            <p className="text-sm text-gray-600 mt-0.5">
              ลบข้อมูลถาวร กู้คืนไม่ได้ — มีผลเฉพาะบริษัท <span className="font-medium">{tenantName || 'นี้'}</span> เท่านั้น
            </p>
          </div>
        </div>
      </div>

      {/* ระดับ 1 — ล้างข้อมูลการทำงาน */}
      <div className="card p-4">
        <div className="font-medium text-gray-900">ล้างข้อมูลการทำงาน</div>
        <p className="text-sm text-gray-600 mt-1">
          ลบรอบเงินเดือน · เวลาเข้า-ออกงาน · ใบลา · OT · สลิป — <span className="font-medium">เก็บพนักงานและการตั้งค่าไว้</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">เหมาะกับ: เริ่มต้นรอบการทำงานใหม่ โดยไม่ต้องเพิ่มพนักงานใหม่</p>
        <button onClick={() => openReset('work')} disabled={loading} className="btn-secondary mt-3 text-red-600 border-red-200 hover:bg-red-50">
          <Trash2 className="w-4 h-4" /> ล้างข้อมูลการทำงาน
        </button>
      </div>

      {/* ระดับ 2 — ล้างข้อมูลทั้งหมด */}
      <div className="card p-4 border-red-200">
        <div className="font-medium text-gray-900">ล้างข้อมูลทั้งหมด</div>
        <p className="text-sm text-gray-600 mt-1">
          ลบข้อมูลการทำงาน <span className="font-medium">+ พนักงานทั้งหมด + การตั้งค่า</span> เหลือบริษัทเปล่าเหมือนเพิ่งเปิดใช้
        </p>
        <p className="text-xs text-gray-400 mt-1">เหมาะกับ: เริ่มต้นใหม่ทั้งหมด (ยังเก็บบัญชีเจ้าของและประวัติการใช้งานไว้)</p>
        <button onClick={() => openReset('all')} disabled={loading} className="btn-danger mt-3">
          <Trash2 className="w-4 h-4" /> ล้างข้อมูลทั้งหมด
        </button>
      </div>

      <Modal open={mode !== null} onClose={close} title={mode === 'all' ? 'ยืนยันล้างข้อมูลทั้งหมด' : 'ยืนยันล้างข้อมูลการทำงาน'}>
        <ConsequenceBody
          intro="เมื่อยืนยัน ระบบจะลบรายการต่อไปนี้ถาวร:"
          lines={lines}
          alerts={[
            {
              level: 'block',
              title: 'ลบถาวร กู้คืนไม่ได้',
              detail: mode === 'all'
                ? 'รวมพนักงานและการตั้งค่าทั้งหมด — ต้องเพิ่มพนักงานและตั้งค่าใหม่หลังล้าง'
                : 'ข้อมูลการทำงานที่ลบจะกู้คืนไม่ได้',
            },
          ]}
        />

        <div className="mt-4">
          <label className="label">
            พิมพ์ชื่อบริษัท “<span className="font-semibold">{tenantName}</span>” เพื่อยืนยัน
          </label>
          <input
            className="input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={tenantName}
            autoComplete="off"
          />
        </div>

        <div className="flex gap-2 mt-5">
          <button className="btn-secondary flex-1" onClick={close}>ยกเลิก</button>
          <button className="btn-danger flex-1" onClick={doReset} disabled={!confirmOk || reset.isPending}>
            {reset.isPending ? 'กำลังล้าง…' : mode === 'all' ? 'ล้างทั้งหมด' : 'ล้างข้อมูลการทำงาน'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
